import { useCallback, useMemo, useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { competitionEngine, tournamentEngine } from 'tods-competition-factory';

import { progressMapAtom } from 'atoms/misc';
import { tournamentAtom, tournamentIdAtom, tournamentsAtom } from 'atoms/tournament';
import { COMPETITION_ENGINE, TOURNAMENT_ENGINE } from 'config/graphql/constants';
import { useTournamentService } from 'hooks/tournament/useTournamentService';
import { type CloudError } from 'repository/CloudError';

/**
 * This is to receive saving status for a saveTournament action by using it's unique identifier (action)
 * @deprecated using promises from the original action is the preferred approach to track loading states
 * @param action unique identifier used in saveTournament call
 * @returns current state of action
 */
export const useProgress = (action: string): boolean => {
  const progressAtom = useMemo(() => selectAtom(progressMapAtom, (progress) => progress[action]), [action]);
  return !!useAtomValue(progressAtom);
};

export const useSaveTournament = () => {
  const setTournaments = useSetAtom(tournamentsAtom);
  const setTournament = useSetAtom(tournamentAtom);
  const updateProgress = useSetAtom(progressMapAtom);
  const tournamentId = useAtomValue(tournamentIdAtom);
  const tournamentService = useTournamentService();
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState('');
  const [error, setError] = useState<CloudError>(undefined);
  const saveTournament = useCallback(
    async (
      action: string,
      engine: typeof TOURNAMENT_ENGINE | typeof COMPETITION_ENGINE,
      methods: { method: string; params }[],
    ) => {
      setSaving(true);
      updateProgress((progress) => ({ ...progress, [action]: true }));
      setAction(action);

      // NOTE: this enables logging of all methods called during saveTournament when there is an internal factory error
      // eslint-disable-next-line
      if (window?.['dev']?.getContext().internal) console.log({ methods });

      const saveTournamentResult = await tournamentService.saveTournament(tournamentId, engine, methods);

      if (saveTournamentResult?.data?.mutateTournament?.success) {
        if (engine === TOURNAMENT_ENGINE) {
          const { tournamentRecord } = tournamentEngine.getTournament({ tournamentId });
          setTournament(tournamentRecord);
        }
        if (engine === COMPETITION_ENGINE) {
          const { tournamentRecords } = competitionEngine.getState();
          setTournaments(tournamentRecords);
        }
        setError(undefined);
      } else {
        // eslint-disable-next-line
        if (window?.['dev']?.getContext().errors) console.log({ result: saveTournamentResult });
        setError(saveTournamentResult?.error);
      }
      setSaving(false);
      updateProgress(({ [action]: _, ...progress }) => progress);
      setAction(undefined);
      return Promise.resolve(saveTournamentResult);
    },
    [setTournament, setTournaments, tournamentService, updateProgress, tournamentId],
  );

  return useMemo(() => ({ action, error, saving, saveTournament }), [action, error, saveTournament, saving]);
};
